import * as React from "react";
import * as _ from "underscore";
import { Button } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { connect } from "react-redux";

import { PropertyRepairShape, DocTypes } from "@eave/shared/types/api";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import APIClient from "../../../APIClient";
import DocDisplay from "../../shared/DocDisplay";
import { HurdleStatus } from "../../HurdleStatus";
import { mapDispatchToProps } from "../selectors";

import RepairEntry from "./RepairEntry";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

interface AppraisalRepairsProps extends HurdleProps {
  loadApp: Function;
  modalTitle: string;
  repairs: {
    map: Function;
    length: number;
    [index: number]: PropertyRepairShape;
  };
  onChange: Function;
}

class AppraisalRepair extends React.Component<AppraisalRepairsProps, any> {
  state = { changesMade: false };

  allRepairsReviewed() {
    return _.all(
      _.map(
        this.props.repairs,
        (r: PropertyRepairShape) =>
          r.in_appraisal === false ||
          (r.in_appraisal === true && r.appraisal_cost_to_cure !== null),
      ),
    );
  }

  noRepairsIndicated() {
    return _.all(_.map(this.props.repairs, (r: PropertyRepairShape) => r.in_appraisal === false));
  }

  needToRequestInspection() {
    return this.allRepairsReviewed() && _.any(_.pluck(this.props.repairs, "in_appraisal"));
  }

  ineligibleRepairsInAppraisal() {
    const repairs = _.chain(this.props.repairs)
      .filter((r: any) => !r.eligible)
      .filter((r: any) => r.in_appraisal)
      .value();
    return repairs.length > 0;
  }

  requestInspectionIfNeeded(): any {
    const { app, hurdle } = this.props;

    if (!this.needToRequestInspection()) {
      return Promise.resolve();
    }

    let repairs;
    if (this.ineligibleRepairsInAppraisal()) {
      repairs = "ineligible repairs and requires an inspection";
    } else {
      repairs = "eligible repairs only, and does not necessarily require an inspection";
    }

    if (
      !confirm(`Property has ${repairs}.
Have you messaged the borrower to ask if they want an inspection?`)
    ) {
      return Promise.reject(new Error("have not yet asked about inspection"));
    }

    return APIClient.put(`/loan_applications/${app.id}/hurdles/property_condition/stage`, {
      requested_inspection: true,
    });
  }

  onChange(updated) {
    if (!this.state.changesMade) {
      this.setState({
        ...this.state,
        changesMade: true,
      });
    }

    this.props.onChange(updated);
  }

  onSave() {
    const { app, loadApp, onHide } = this.props;

    const params = _.map(this.props.repairs, (r: PropertyRepairShape) => ({
      repair_type: r.repair_type,
      in_appraisal: r.in_appraisal,
      appraisal_cost_to_cure: r.appraisal_cost_to_cure,
      appraisal_note: r.appraisal_note,
    }));

    this.requestInspectionIfNeeded()
      .then(() =>
        APIClient.put(
          `/loan_applications/${app.id}/hurdles/property_condition/sync_appraisal_repairs`,
          params,
        ),
      )
      .then(() => {
        loadApp(app.id);

        if (!this.needToRequestInspection()) {
          onHide(); // if saving incomplete list
        }
      });
  }

  renderStatusText() {
    let text;
    let effectiveStatus;

    if (this.noRepairsIndicated()) {
      text = "No repairs indicated; save to finish";
      effectiveStatus = "green";
    } else if (this.allRepairsReviewed()) {
      text = "Repair(s) indicated; save to message borrower for inspection report";
      effectiveStatus = "yellow";
    } else {
      text = "Review repairs; mark whether indicated, and cost to cure";
      effectiveStatus = "yellow";
    }

    return (
      <div className="property-condition__status">
        <HurdleStatus status={effectiveStatus} />
        <span style={{ marginLeft: "0.75rem" }}>{text}</span>
      </div>
    );
  }

  render() {
    const { app, hurdle, onHide, modalTitle, repairs } = this.props;

    const appraisalDocs = _.filter(app.documents, d => d.type === DocTypes.AppraisalReport);

    let content;
    if (!appraisalDocs.length) {
      content = (
        <div className="property-condition__container--no-flex">
          <div className="property-condition__status">
            <HurdleStatus status="red" />
            <span style={{ marginLeft: "0.75rem" }}>
              {app.appraisal_ordered
                ? "Appraisal ordered; waiting for response"
                : "Waiting on operator to order appraisal"}
            </span>
          </div>
        </div>
      );
    } else {
      content = (
        <div className="property-condition__container">
          <div className="property-condition__main">
            {this.renderStatusText()}
            <DocDisplay docs={appraisalDocs} />
          </div>
          <div className="property-condition__sidebar">
            <RepairEntry prefix="appraisal" repairs={repairs} onChange={this.onChange.bind(this)} />
          </div>
        </div>
      );
    }

    let footer;
    if (this.state.changesMade || this.allRepairsReviewed()) {
      footer = (
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      );
    } else {
      footer = <></>;
    }

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        {content}
        {footer}
      </HurdleView>
    );
  }
}

export default connect<any, any, any>(null, mapDispatchToProps)(AppraisalRepair);
